/**
 * Time math for the timed-exam countdown.
 *
 * Pure and epoch-ms based so it can be tested without a clock or a component. The
 * rendering lives in ExamCountdown, which owns its own tick — deliberately NOT in
 * ExamScreen, whose re-renders cascade into question localisation and previously caused
 * 48-69 second INP freezes.
 */

/** Warn the student at this much time left. */
export const LOW_TIME_WARNING_MS = 5 * 60 * 1000;

/**
 * Milliseconds left, floored at 0 — or `null` when the deadline is UNKNOWABLE.
 *
 * `null`, not 0, is load-bearing. A corrupt stored `startTime` yields an invalid Moment
 * (`moment()` does not throw on garbage), so `valueOf()` is NaN. Returning 0 there would
 * render a permanent, normal-styled "0:00" while `moment().diff(<invalid>)` is also NaN
 * — so `NaN >= examTimeMinutes` is false and the exam NEVER auto-submits. The student
 * would watch a clock reading zero while the exam ran forever. "Unknown" and "expired"
 * must not share a representation.
 */
export function getExamTimeRemainingMs(
  startTimeMs: number,
  examTimeMinutes: number,
  nowMs: number,
): number | null {
  if (
    !Number.isFinite(startTimeMs) ||
    !Number.isFinite(examTimeMinutes) ||
    !Number.isFinite(nowMs)
  ) {
    return null;
  }
  const endMs = startTimeMs + examTimeMinutes * 60_000;
  return Math.max(0, endMs - nowMs);
}

export function isLowTime(remainingMs: number | null): boolean {
  return remainingMs !== null && remainingMs > 0 && remainingMs <= LOW_TIME_WARNING_MS;
}

/**
 * The single definition of "time is up" — used by BOTH the display and the auto-submit,
 * so the two cannot drift apart. An unknown deadline is NOT expired: treating it as
 * expired would auto-submit an exam whose clock we simply failed to read.
 */
export function isExpired(remainingMs: number | null): boolean {
  return remainingMs !== null && remainingMs <= 0;
}

/**
 * Discrete minute thresholds to announce, so a screen-reader user is warned without the
 * per-second timer being read aloud. Descending; the first one crossed wins.
 */
export const TIME_WARNING_THRESHOLDS_MIN = [30, 15, 5, 1] as const;

/**
 * The threshold just crossed, or null. Announce only when this CHANGES — announcing on
 * every tick would defeat the point of keeping the timer silent.
 */
export function getTimeWarningThresholdMin(
  remainingMs: number | null,
): number | null {
  if (remainingMs === null || remainingMs <= 0) return null;

  const remainingMin = remainingMs / 60_000;
  // Thresholds descend, so the LAST one still satisfied is the tightest —
  // 4 minutes left is "5 minutes", not "30".
  let crossed: number | null = null;
  for (const threshold of TIME_WARNING_THRESHOLDS_MIN) {
    if (remainingMin <= threshold) crossed = threshold;
  }
  return crossed;
}

/**
 * `H:MM:SS` past an hour, `M:SS` below it.
 *
 * Rounds UP: with 500ms left a student should see "0:01", not "0:00" — showing zero
 * while the exam is still accepting answers reads as a frozen or broken clock.
 */
export function formatCountdown(remainingMs: number | null): string {
  // An em-dash for "unknown". A broken clock must never impersonate an expired one.
  if (remainingMs === null) return "\u2014";

  const totalSeconds = Math.ceil(Math.max(0, remainingMs) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");

  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`;
}
